import {useQuery} from '@tanstack/react-query'
import {customersApiClient} from '@/services/apiClient'

/**
 * `GET /passengers/trips/{trip}/waiting-status` — the waiting clock at the pickup, as the
 * passenger sees it. The mirror of the driver portal's `useWaitingStatus`, same shape, same
 * rate, so the two screens show the same figure.
 *
 * Polled rather than pushed: a driver standing at a kerb generates no events. The local
 * tick in `WaitingTimer` carries the digits between polls; the poll is what picks up the
 * clock starting, `enabled` and `finalized`.
 *
 * `server_time` and the moment the answer arrived are stamped onto `live`, so the timer
 * can anchor to the server's figures rather than trust this device's clock.
 */
export function useWaitingStatus(tripId, {enabled = true} = {}) {
    return useQuery({
        queryKey: ['passenger-waiting-status', String(tripId)],
        enabled: !!tripId && enabled,
        refetchInterval: 2000,
        // No point polling a tab nobody is looking at — but refetch the moment they come
        // back, because that is exactly when the number is being checked.
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        queryFn: async () => {
            const result = await customersApiClient.get(`/trips/${tripId}/waiting-status`)
            if (!result.success) throw new Error(result.error)
            const waiting = result.data?.waiting ?? result.data
            const serverTime = result.data?.server_time ?? waiting?.server_time ?? waiting?.live?.server_time ?? null
            if (waiting?.live) {
                return {...waiting, live: {...waiting.live, server_time: serverTime, received_at_ms: Date.now()}}
            }
            return waiting
        },
    })
}

export default useWaitingStatus
